export type ByteUnit = "b" | "kb" | "mb" | "gb" | "tb" | "pb" | "k" | "m" | "g" | "t" | "p"
export type ByteInput = `${number}${ByteUnit}` | `${number}` | number
export type ByteBase = 1000 | 1024

export interface ByteConvertOptions {
  /** Base for calculations: 1024 (binary) or 1000 (decimal). Default: 1024 */
  base: ByteBase
  /** Whether to round the result to the nearest integer. Default: true */
  round: boolean
}

export interface FormatBytesOptions {
  base?: ByteBase
  precision?: number
  /** Use full unit names e.g. "bytes", not "B". Default: false */
  verbose?: boolean
}

export const BYTE_REGEX = /^([+-]?(?:[0-9]*\.?[0-9]+(?:[eE][+-]?[0-9]+)?|Infinity))\s*([a-zA-Z]*)$/i

type UnitMultiplierEntry = 1 | ((base: ByteBase) => number)

const UNIT_MULTIPLIERS: Record<string, UnitMultiplierEntry> = {
  "": 1,
  b: 1,
  k: (base) => base,
  kb: (base) => base,
  m: (base) => base ** 2,
  mb: (base) => base ** 2,
  g: (base) => base ** 3,
  gb: (base) => base ** 3,
  t: (base) => base ** 4,
  tb: (base) => base ** 4,
  p: (base) => base ** 5,
  pb: (base) => base ** 5,
} as const

const SUPPORTED_UNITS = Object.keys(UNIT_MULTIPLIERS)
  .filter((u) => u !== "")
  .join(", ")

/**
 * Converts a byte string or number to raw bytes.
 *
 * @example
 * toBytes("1kb")                    // 1024
 * toBytes("1mb", { base: 1000 })   // 1000000
 * toBytes("2.5gb")                  // 2684354560
 * toBytes(1024)                     // 1024
 */
export function toBytes(input: ByteInput, options: Partial<ByteConvertOptions> = {}): number {
  const { base = 1024, round = true } = options

  const normalizedInput = String(input).toLowerCase().trim()
  const match = normalizedInput.match(BYTE_REGEX)

  if (!match) {
    throw new Error(`Invalid input: "${input}". Expected a number or formatted string like "2.5kb", "33mb"`)
  }

  const value = parseFloat(match[1])
  const unit = match[2] as keyof typeof UNIT_MULTIPLIERS

  if (!Number.isFinite(value)) {
    throw new Error(`Non-finite values are not supported: "${match[1]}"`)
  }

  if (value < 0) {
    throw new Error(`Negative values are not supported: "${value}"`)
  }

  const multiplierEntry = UNIT_MULTIPLIERS[unit]

  if (multiplierEntry === undefined) {
    throw new Error(`Unsupported unit: "${unit}". Supported units: ${SUPPORTED_UNITS}`)
  }

  const multiplier = typeof multiplierEntry === "function" ? multiplierEntry(base) : multiplierEntry
  const result = value * multiplier

  return round ? Math.round(result) : result
}

const SHORT_UNITS = ["B", "KB", "MB", "GB", "TB", "PB"] as const
const LONG_UNITS = ["bytes", "KB", "MB", "GB", "TB", "PB"] as const

/**
 * Formats a raw byte count into a human-readable string.
 *
 * @example
 * formatBytes(1536)                        // "1.50 KB"
 * formatBytes(1500, { base: 1000 })        // "1.50 KB"
 * formatBytes(1024, { verbose: true })     // "1.00 KB"
 * formatBytes(0)                           // "0 B"
 */
export function formatBytes(bytes: number, options: FormatBytesOptions = {}): string {
  const { base = 1024, precision = 2, verbose = false } = options
  const units = verbose ? LONG_UNITS : SHORT_UNITS

  if (bytes < 0) throw new Error(`Negative values are not supported: "${bytes}"`)
  if (bytes === 0) return `0 ${units[0]}`
  if (bytes < 1) return `${bytes.toFixed(precision)} ${units[0]}`

  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(base)), units.length - 1)

  const value = bytes / Math.pow(base, unitIndex)
  return `${value.toFixed(precision)} ${units[unitIndex]}`
}

/** Creates a pre-configured `toBytes` converter with fixed defaults. */
export function createByteConverter(defaultOptions: Partial<ByteConvertOptions> = {}) {
  return (input: ByteInput, overrides?: Partial<ByteConvertOptions>): number =>
    toBytes(input, { ...defaultOptions, ...overrides })
}
